import { Fragment, useMemo, useState } from 'react';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import {
  useCreateEndpointTestRunGroup,
  useDeleteEndpointTestRunGroup,
  useEndpointTestRunGroup,
  useEndpointTestRunGroups,
  useExportEndpointTestRunGroup,
  useFetchEndpointTestRunGroup,
  isTestRunning,
} from '@/hooks/useEndpointTestRunGroups';
import { useEndpointTests } from '@/hooks/useEndpointTests';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { UserName } from '@/components/ui/UserName';
import { formatRelativeTime } from '@/utils/dateUtils';
import {
  dayHeader,
  failedCount,
  groupSummary,
  matchesOrigin,
  needsReview,
  originPhrase,
  summarizeResults,
  targetLabel,
  testNumberLabel,
} from '@/utils/testRunResults';
import { useEndpointTestSuites } from '@/hooks/useEndpointTestEvals';
import { IN_FLIGHT_TOAST_TITLE, inFlightRefusal, inFlightToastDetail } from '@/utils/apiRefusal';
import { TestRunSummaryStrip } from './TestRunSummaryStrip';
import { TestRunDetail } from './TestRunDetail';
import { TestInProgressCard } from './TestInProgressCard';
import type { Endpoint } from '@/types';
import type { EndpointTestRunGroup, EndpointTestRunGroupSummary } from '@/types/endpointTests';


/**
 * The strip a row shows: the list's summary, or — while the row is open — the
 * counts of the results the detail is polling, so an executing test run counts
 * up as results arrive. Same query key as `TestRunDetail`, so no second request.
 */
function useRowSummary(group: EndpointTestRunGroup, open: boolean): EndpointTestRunGroupSummary {
  const { data } = useEndpointTestRunGroup(open ? group.id : undefined);
  if (open && data) {
    // The detail's own completedAt / refusals: it polls faster than the list.
    const current = summarizeResults(data.results, data);
    return { ...current, duration: current.duration ?? group.summary?.duration ?? null };
  }
  return groupSummary(group);
}

function RowStrip({ group, open, children }: { group: EndpointTestRunGroup; open: boolean; children: React.ReactNode }) {
  const summary = useRowSummary(group, open);
  // Per-run cost's lever (task 1253): off, the row shows no cost item.
  const costEnabled = useFlag('innovate.fe.analytics.per-run-cost');
  return (
    <TestRunSummaryStrip
      summary={summary}
      environment={group.environment}
      version={group.resourceVersion}
      cost={costEnabled ? group.cost : null}
    >
      {children}
    </TestRunSummaryStrip>
  );
}

type ResultFilter = 'all' | 'failed' | 'needs-review';

/** The environment filter's value for "working state". */
const WORKING_STATE = '__working__';

interface TestRunsListProps {
  endpoint: Endpoint;
  /** The open test run (controlled, so a new one can be opened from the toolbar). */
  openId?: string | null;
  onOpenChange?: (id: string | null) => void;
  /**
   * Open the Compare section with this test as the candidate (task 1227); the
   * section picks the baseline from the compare suggestion. Absent = no Compare.
   */
  onCompare?: (b: string) => void;
}

const actionClass =
  'inline-flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50';

/**
 * The Testing tab's **Tests** section (task 1177; titled "Test runs" until
 * task 1227), newest first — one row per test (an `EndpointTestRunGroup`) with
 * its summary strip, who ran it and when, and its actions. The compare
 * checkboxes are gone (task 1227): Compare is its own section. Replaces the generic collaboration history on the service page; the
 * Advanced endpoint hub keeps that history.
 */
export function TestRunsList(props: TestRunsListProps) {
  // The test runs feature set's lever (task 1215): off, no list and no fetch.
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  return runsEnabled ? <TestRunsListContent {...props} /> : null;
}

function TestRunsListContent({
  endpoint,
  openId: controlledOpenId,
  onOpenChange,
  onCompare,
}: TestRunsListProps) {
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [testFilter, setTestFilter] = useState('');
  /** '' = any; an origin; `reruns`; or `suite:<id>` (task 1232). */
  const [originFilter, setOriginFilter] = useState('');
  const [deleting, setDeleting] = useState<EndpointTestRunGroup | null>(null);
  const [localOpenId, setLocalOpenId] = useState<string | null>(null);
  const openId = controlledOpenId !== undefined ? controlledOpenId : localOpenId;
  const setOpenId = (id: string | null) => {
    setLocalOpenId(id);
    onOpenChange?.(id);
  };

  const { data, isLoading, isError } = useEndpointTestRunGroups(endpoint.id, {
    endpointTestId: testFilter || undefined,
  });
  const { data: testsData } = useEndpointTests(endpoint.id);
  const createTestRun = useCreateEndpointTestRunGroup();
  const deleteTestRun = useDeleteEndpointTestRunGroup();
  const exportTestRun = useExportEndpointTestRunGroup();
  const fetchDetail = useFetchEndpointTestRunGroup();
  const toast = useToast();

  const groups = useMemo(() => data?.data ?? [], [data]);
  const tests = testsData?.data ?? [];
  const { data: suites = [] } = useEndpointTestSuites(endpoint.id);
  // For the identity line (task 1232): a single-scenario test's name, a re-run's parent number.
  const scenarioNames = useMemo(() => new Map(tests.map((test) => [test.id, test.name])), [tests]);
  const numbers = useMemo(() => new Map(groups.map((group) => [group.id, group.number])), [groups]);

  // Environments that appear in this endpoint's test runs, for the filter.
  const environments = useMemo(() => {
    const seen = new Map<string, string>();
    for (const group of groups) {
      if (group.environmentId) seen.set(group.environmentId, group.environment?.path ?? group.environmentId);
    }
    return [...seen.entries()];
  }, [groups]);

  // Running tests are cards above the list (task 1233), newest first, and
  // never also rows (task 1232). The list below is what finished.
  const running = groups.filter(isTestRunning);
  const settled = useMemo(() => groups.filter((group) => !isTestRunning(group)), [groups]);
  const visible = groups.filter((group) => {
    if (isTestRunning(group)) return false;
    if (!matchesOrigin(group, originFilter)) return false;
    if (environmentFilter === WORKING_STATE && group.environmentId !== null) return false;
    if (environmentFilter && environmentFilter !== WORKING_STATE && group.environmentId !== environmentFilter) return false;
    const summary = groupSummary(group);
    if (resultFilter === 'failed' && failedCount(summary) === 0) return false;
    if (resultFilter === 'needs-review' && !needsReview(summary)) return false;
    return true;
  });

  const rerun = async (group: EndpointTestRunGroup, failedOnly: boolean) => {
    try {
      const detail = await fetchDetail(group.id);
      const refused = (detail.refusals ?? []).map((refusal) => refusal.testId);
      // What was asked for (task 1228), else what landed (rows made before the column).
      const requested = detail.testIds?.length
        ? detail.testIds
        : [...new Set([...detail.results.map((result) => result.endpointTestId), ...refused])];
      const created = await createTestRun.mutateAsync({
        endpointId: endpoint.id,
        // Re-run failed sends what the test asked for with `failedOnly`: the
        // service narrows it to the scenarios that did not pass, its refusals
        // included (task 1239), and records `rerun_failed` (task 1231).
        testIds: requested,
        environmentId: group.environmentId,
        concurrency: group.concurrency,
        // Every test run is graded (task 1185), including a re-run of one made
        // while the grading toggle existed and was off.
        graded: true,
        parentEndpointTestRunGroupId: group.id,
        ...(failedOnly ? { failedOnly: true } : {}),
      });
      toast.success(failedOnly ? 'Re-running failed scenarios' : 'Re-running test');
      setOpenId(created.id);
    } catch (err) {
      const names = inFlightRefusal(err);
      if (names) toast.error(IN_FLIGHT_TOAST_TITLE, inFlightToastDetail(names));
      else toast.error('Re-run failed to start', err instanceof Error ? err.message : undefined);
    }
  };

  const handleExport = async (group: EndpointTestRunGroup) => {
    try {
      await exportTestRun.mutateAsync({ id: group.id, format: 'csv' });
    } catch (err) {
      toast.error('Export failed', err instanceof Error ? err.message : undefined);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTestRun.mutateAsync({ id: deleting.id, endpointId: endpoint.id });
      toast.success('Test deleted');
      if (openId === deleting.id) setOpenId(null);
      setDeleting(null);
    } catch (err) {
      toast.error('Failed to delete test', err instanceof Error ? err.message : undefined);
    }
  };

  const segmentClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium border -ml-px first:ml-0 first:rounded-l-md last:rounded-r-md ${
      active
        ? 'bg-indigo-600 border-indigo-600 text-white'
        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
    }`;
  const selectClass =
    'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white';

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
      data-testid="test-runs-list"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <ClockIcon className="h-5 w-5 mr-2 text-gray-400" />
          Tests{groups.length > 0 && <span className="ml-1.5 text-gray-400">· {groups.length}</span>}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Filter by environment"
            value={environmentFilter}
            onChange={(e) => setEnvironmentFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">All environments</option>
            <option value={WORKING_STATE}>Working state</option>
            {environments.map(([id, path]) => (
              <option key={id} value={id}>{path}</option>
            ))}
          </select>
          <span className="inline-flex" role="group" aria-label="Filter by result">
            <button type="button" aria-pressed={resultFilter === 'all'} onClick={() => setResultFilter('all')} className={segmentClass(resultFilter === 'all')}>
              All results
            </button>
            <button type="button" aria-pressed={resultFilter === 'failed'} onClick={() => setResultFilter('failed')} className={segmentClass(resultFilter === 'failed')}>
              Failed
            </button>
            <button type="button" aria-pressed={resultFilter === 'needs-review'} onClick={() => setResultFilter('needs-review')} className={segmentClass(resultFilter === 'needs-review')}>
              Needs review
            </button>
          </span>
          <select
            aria-label="Filter by scenario"
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">All scenarios</option>
            {tests.map((test) => (
              <option key={test.id} value={test.id}>{test.name}</option>
            ))}
          </select>
          <select
            aria-label="Filter by origin"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">Any origin</option>
            {suites.map((suite) => (
              <option key={suite.id} value={`suite:${suite.id}`}>{`Suite ${suite.name}`}</option>
            ))}
            <option value="assistant">Assistant</option>
            <option value="selection">Selection</option>
            <option value="single">Single scenario</option>
            <option value="reruns">Re-runs</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading tests…</p>
      ) : isError ? (
        <p className="p-4 text-sm text-red-500 dark:text-red-400">Couldn’t load tests.</p>
      ) : groups.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="test-runs-empty">
          No tests yet. Select scenarios above and run them; each test appears here.
        </p>
      ) : (
        <>
        {running.length > 0 && (
          <ul data-testid="tests-in-progress">
            {running.map((group) => (
              <TestInProgressCard
                key={group.id}
                endpointId={endpoint.id}
                test={group}
                settled={settled}
                scenarioNames={scenarioNames}
                open={openId === group.id}
                onToggle={() => setOpenId(openId === group.id ? null : group.id)}
                onDelete={() => setDeleting(group)}
              >
                <TestRunDetail endpointId={endpoint.id} testRunId={group.id} />
              </TestInProgressCard>
            ))}
          </ul>
        )}
        {visible.length === 0 ? (
          settled.length > 0 && (
            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No tests match these filters.</p>
          )
        ) : (
        <ul>
          {visible.map((group, index) => {
            const failures = failedCount(groupSummary(group));
            const open = openId === group.id;
            // Day headers (task 1232), in the User's timezone; the list is newest first.
            const day = dayHeader(group.createdAt);
            const startsDay = index === 0 || dayHeader(visible[index - 1].createdAt) !== day;
            const phrase = originPhrase(group, scenarioNames, numbers);
            const number = testNumberLabel(group);
            return (
              <Fragment key={group.id}>
              {startsDay && (
                <li
                  data-testid="test-runs-day"
                  className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700"
                >
                  {day}
                </li>
              )}
              <li data-testid={`test-run-${group.id}`} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    {/* The identity line (task 1232): #N · time · target · origin · author. */}
                    <p data-testid={`test-identity-${group.id}`} className="text-xs text-gray-600 dark:text-gray-300">
                      {number && <span className="font-semibold text-gray-900 dark:text-white">{number} · </span>}
                      <time dateTime={group.createdAt} title={formatRelativeTime(group.createdAt)}>
                        {new Date(group.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </time>
                      {' · '}{targetLabel(group)}
                      {phrase && <> · {phrase}</>}
                      {' · '}<UserName userId={group.createdBy} />
                    </p>
                    <RowStrip group={group} open={open}>
                      <button type="button" onClick={() => void rerun(group, false)} disabled={createTestRun.isPending} className={actionClass}>
                        <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                        Re-run
                      </button>
                      {failures > 0 && (
                        <button type="button" onClick={() => void rerun(group, true)} disabled={createTestRun.isPending} className={actionClass}>
                          {`Re-run failed (${failures})`}
                        </button>
                      )}
                      {onCompare && (
                        <button type="button" onClick={() => onCompare(group.id)} className={actionClass}>
                          Compare
                        </button>
                      )}
                      <button type="button" onClick={() => void handleExport(group)} disabled={exportTestRun.isPending} className={actionClass}>
                        Export
                      </button>
                      <button type="button" onClick={() => setDeleting(group)} className="text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400">
                        Delete
                      </button>
                      <button type="button" onClick={() => setOpenId(open ? null : group.id)} aria-expanded={open} className={`${actionClass} ml-auto`}>
                        {open ? 'Close' : 'Open'}
                      </button>
                    </RowStrip>
                  </div>
                </div>
                {open && (
                  <TestRunDetail endpointId={endpoint.id} testRunId={group.id} />
                )}
              </li>
              </Fragment>
            );
          })}
        </ul>
        )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete test"
        message="Delete this test? Its results leave the Tests list; the collaboration runs it produced stay in the run history."
        confirmLabel="Delete"
        confirmStyle="danger"
        isLoading={deleteTestRun.isPending}
      />
    </div>
  );
}

export default TestRunsList;
